export const AGE_GROUP_OPTIONS = ["PUP", "YOUNG", "ADULT", "OLD"];
export const GENDER_OPTIONS = ["MALE", "FEMALE", "UNKNOWN"];
export const BREED_OPTIONS = ["INDIE", "MIXED", "OTHER"];
export const COLOR_OPTIONS = ["BROWN", "BLACK", "WHITE", "OTHER"];
export const COAT_PATTERN_OPTIONS = ["SOLID", "TRICOLOR", "TIGER", "ZEBRA", "MIXED"];
export const BODY_SIZE_OPTIONS = ["SMALL", "MEDIUM", "LARGE"];
export const BODY_CONDITION_OPTIONS = ["POOR", "FAIR", "GOOD"];
export const REPRODUCTIVE_OPTIONS = ["PREGNANT", "IN_HEAT", "LACTATING", "NA"];
export const IDENTIFICATION_OPTIONS = ["COLLAR", "NOTCH"];
export const REGION_OPTIONS = ["HEAD", "LIMB", "BODY", "TAIL"];
export const INJURY_OPTIONS = ["HEAD", "LIMB", "BODY", "TAIL", "NO_VISIBLE_INJURY"];

export const REGION_SELECT_OPTIONS = REGION_OPTIONS.map((v) => ({
  value: v,
  label: v,
}));

export const INJURY_SELECT_OPTIONS = INJURY_OPTIONS.map((v) => ({
  value: v,
  label: v.replace(/_/g, " "),
}));

export const multiEnumSelectStyles = {
  control: (base) => ({ ...base, minHeight: 38 }),
  menuPortal: (base) => ({ ...base, zIndex: 10002 }),
};

const plainObj = (val) => {
  if (val == null) return {};
  if (typeof val === "object" && !Array.isArray(val)) return { ...val };
  return {};
};

const normalizeIdentificationString = (fields) => {
  const id = fields?.identification;
  if (Array.isArray(id)) {
    const found = id.filter((x) => IDENTIFICATION_OPTIONS.includes(x));
    return found[0] || "";
  }
  if (typeof id === "string") {
    return IDENTIFICATION_OPTIONS.includes(id) ? id : "";
  }
  if (id != null && typeof id === "object" && !Array.isArray(id)) {
    if (IDENTIFICATION_OPTIONS.includes(id.collarStatus)) return id.collarStatus;
    if (id.earNotch != null && String(id.earNotch).length > 0) return "NOTCH";
  }
  return "";
};

const filterToEnum = (arr, allowed) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter((x) => allowed.includes(x));
};

export const emptyDogForm = () => ({
  demographics: {
    ageGroup: "",
    gender: "",
    breed: "",
    breedOther: "",
  },
  appearance: {
    primaryColor: "",
    primaryColorOther: "",
    secondaryColor: "",
    secondaryColorOther: "",
    coatPattern: "",
    mixed: "",
  },
  body: {
    size: "",
    bodyCondition: "",
  },
  reproductiveStatus: "",
  identification: "",
  scars: [],
  spots: [],
  injuries: [],
  remarks: "",
});

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

/** Filter form prefilled with gender, breed, and primary color only. */
export const dogToFilterForm = (dog) => {
  const form = emptyDogForm();
  const gender = dog?.fields?.demographics?.gender;
  const breed = dog?.fields?.demographics?.breed;
  const primaryColor = dog?.fields?.appearance?.primaryColor;
  if (GENDER_OPTIONS.includes(gender)) form.demographics.gender = gender;
  if (BREED_OPTIONS.includes(breed)) form.demographics.breed = breed;
  if (COLOR_OPTIONS.includes(primaryColor)) {
    form.appearance.primaryColor = primaryColor;
  }
  return form;
};

/** Drop empty fields so the dogs query only constrains what the user set. */
export const compactDogFilter = (form) => {
  if (!form) return {};
  const filter = {};

  const demographics = {};
  if (form.demographics?.ageGroup) {
    demographics.ageGroup = form.demographics.ageGroup;
  }
  if (form.demographics?.gender) demographics.gender = form.demographics.gender;
  if (form.demographics?.breed) demographics.breed = form.demographics.breed;
  if (form.demographics?.breed === "OTHER") {
    const breedOther = trimmed(form.demographics.breedOther);
    if (breedOther) demographics.breedOther = breedOther;
  }
  if (Object.keys(demographics).length) filter.demographics = demographics;

  const appearance = {};
  if (form.appearance?.primaryColor) {
    appearance.primaryColor = form.appearance.primaryColor;
  }
  if (form.appearance?.primaryColor === "OTHER") {
    const primaryColorOther = trimmed(form.appearance.primaryColorOther);
    if (primaryColorOther) appearance.primaryColorOther = primaryColorOther;
  }
  if (form.appearance?.secondaryColor) {
    appearance.secondaryColor = form.appearance.secondaryColor;
  }
  if (form.appearance?.secondaryColor === "OTHER") {
    const secondaryColorOther = trimmed(form.appearance.secondaryColorOther);
    if (secondaryColorOther) {
      appearance.secondaryColorOther = secondaryColorOther;
    }
  }
  if (form.appearance?.coatPattern) {
    appearance.coatPattern = form.appearance.coatPattern;
  }
  const mixed = trimmed(form.appearance?.mixed);
  if (mixed) appearance.mixed = mixed;
  if (Object.keys(appearance).length) filter.appearance = appearance;

  const body = {};
  if (form.body?.size) body.size = form.body.size;
  if (form.body?.bodyCondition) {
    body.bodyCondition = form.body.bodyCondition;
  }
  if (Object.keys(body).length) filter.body = body;

  if (form.reproductiveStatus) {
    filter.reproductiveStatus = form.reproductiveStatus;
  }
  if (form.identification) filter.identification = form.identification;
  if (form.scars?.length) filter.scars = [...form.scars];
  if (form.spots?.length) filter.spots = [...form.spots];
  if (form.injuries?.length) filter.injuries = [...form.injuries];
  const remarks = trimmed(form.remarks);
  if (remarks) filter.remarks = remarks;

  return filter;
};

export const dogToEditForm = (dog) => {
  const f = dog.fields || {};
  const demographics = {
    ageGroup: "",
    gender: "",
    breed: "",
    breedOther: "",
    ...plainObj(f.demographics),
  };
  if (demographics.breed !== "OTHER") {
    demographics.breedOther = "";
  }
  const appearance = {
    primaryColor: "",
    primaryColorOther: "",
    secondaryColor: "",
    secondaryColorOther: "",
    coatPattern: "",
    mixed: "",
    ...plainObj(f.appearance),
  };
  if (appearance.primaryColor !== "OTHER") {
    appearance.primaryColorOther = "";
  }
  if (appearance.secondaryColor !== "OTHER") {
    appearance.secondaryColorOther = "";
  }

  return {
    demographics,
    appearance,
    body: {
      size: "",
      bodyCondition: "",
      ...plainObj(f.body),
    },
    reproductiveStatus: f.reproductiveStatus ?? dog.reproductiveStatus ?? "",
    identification: normalizeIdentificationString(f),
    scars: filterToEnum(f.scars, REGION_OPTIONS),
    spots: filterToEnum(f.spots, REGION_OPTIONS),
    injuries: filterToEnum(f.injuries, INJURY_OPTIONS),
    remarks: f.remarks ?? dog.remarks ?? "",
  };
};

export const buildPatchPayload = (form) => {
  const demographics = { ...form.demographics };
  if (demographics.breed !== "OTHER") {
    demographics.breedOther = "";
  }
  const appearance = { ...form.appearance };
  if (appearance.primaryColor !== "OTHER") {
    appearance.primaryColorOther = "";
  }
  if (appearance.secondaryColor !== "OTHER") {
    appearance.secondaryColorOther = "";
  }
  const payload = {
    demographics,
    appearance,
    body: { ...form.body },
    scars: [...form.scars],
    spots: [...form.spots],
    injuries: [...form.injuries],
    remarks: form.remarks ?? "",
  };
  if (form.reproductiveStatus) {
    payload.reproductiveStatus = form.reproductiveStatus;
  }
  if (form.identification) {
    payload.identification = filterToEnum(
      Array.isArray(form.identification)
        ? form.identification
        : [form.identification],
      IDENTIFICATION_OPTIONS,
    );
  }
  return payload;
};
